<template>
  <div class="patient-timed-release--wrap">
    <div class="patient-timed-release">
      <preview-zoom-dialog
        :dialogType="dialogType"
        :imageSrc="imageSrc"
        :dialogFile="dialogFile"
        :dialogFileMlUrl="dialogFileMlUrl"
        :name="imageName"
        @reload-resource="reloadImageResource"
        @close-dialog="closeMediaDialog"
      />
      <v-container>
        <v-row class="fill-height">
          <v-col>
            <calendar
              :selected-date="selectedDate"
              :mark-weeks="true"
              :dot-marked-dates="timedReleaseMessagesDates"
              @selectDate="selectDate"
              @getCalendar="selectCalendar"
            />
            <v-sheet>
              <div class="navbar">
                <v-btn
                  class="template-button"
                  color="accent"
                  rounded
                  elevation="0"
                  @click="showTemplatesDialog = true"
                  >Browse and apply sprints
                  <v-icon>bhi-calendar</v-icon>
                </v-btn>
                <span class="patient-timed-release-tz" v-if="currentPatient.tz"
                  >Participants timezone: {{ currentPatient.tz }}</span
                >
                <v-btn icon text color="accent" elevation="0" @click="prevWeek"
                  ><v-icon>bhi-arrow-left</v-icon></v-btn
                >
                <v-btn icon text color="accent" elevation="0" @click="nextWeek"
                  ><v-icon>bhi-arrow-right</v-icon></v-btn
                >
              </div>
              <div class="patient-timed-release__calendar-wrapper">
                <v-calendar
                  ref="calendar"
                  v-model="dateFormattedAPI"
                  type="week"
                  :events="events"
                  :event-ripple="false"
                  :event-color="getEventColor"
                  :event-overlap-threshold="120"
                  first-interval="0"
                  interval-count="25"
                  locale="en"
                  interval-width="50"
                  :short-weekdays="false"
                  :class="dragEvent ? 'dragBorder' : ''"
                  @change="loadMessages"
                  @mousedown:event="startDrag"
                  @mousedown:time="startTime"
                  @mousemove:time="mouseMove"
                  @mouseup:time="endDrag"
                  @mouseleave.native="cancelDrag"
                >
                  <template v-slot:event="{ event, timed, eventParsed }">
                    <v-menu
                      rounded
                      absolute
                      offset-x
                      transition="false"
                      :position-x="contextMenuX"
                      :position-y="contextMenuY"
                      v-model="
                        showContextMenu[
                          `${event.plannedStart}-${event.definitionId}`
                        ]
                      "
                      v-if="isRoleAllowed"
                    >
                      <template v-slot:activator="{}">
                        <div
                          class="v-event-timed--wrapper"
                          @click.stop="() => {}"
                          @mousedown="startClickOpenContext(event)"
                          @mouseup="stopClickOpenContext($event)"
                          @mouseleave="stopClickOpenContext($event)"
                        >
                          <div
                            :key="
                              `${event.definitionId}-${event.error}-${event.status}`
                            "
                            class="v-event-draggable"
                          >
                            <v-tooltip
                              :disabled="!!dragEvent"
                              bottom
                              open-delay="500"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <span
                                  v-bind="attrs"
                                  v-on="on"
                                  class="v-event-name"
                                  >{{ event.name }}</span
                                >
                              </template>
                              <span>{{ event.name }}</span>
                            </v-tooltip>
                            <div
                              v-if="!event.sentTime && !event.error"
                              class="v-event-time"
                            >
                              {{
                                formatEventTime(
                                  eventParsed.start.date +
                                    'T' +
                                    eventParsed.start.time,
                                )
                              }}
                            </div>
                            <v-tooltip
                              :disabled="!!dragEvent"
                              v-else-if="!event.error"
                              bottom
                              open-delay="500"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <div
                                  v-bind="attrs"
                                  v-on="on"
                                  class="v-event-time"
                                >
                                  {{
                                    formatEventTime(
                                      eventParsed.start.date +
                                        'T' +
                                        eventParsed.start.time,
                                    )
                                  }}
                                </div>
                              </template>
                              <span v-if="event.status == 'sent'"
                                >sent {{ event.sentTime }}</span
                              >
                            </v-tooltip>
                            <v-tooltip
                              :disabled="!!dragEvent"
                              v-else
                              bottom
                              open-delay="500"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <span
                                  v-bind="attrs"
                                  v-on="on"
                                  class="v-event-time"
                                  >message not sent</span
                                >
                              </template>
                              <span>{{ event.error }}</span>
                            </v-tooltip>
                          </div>
                          <span
                            class="status"
                            :class="
                              `${event.status} ${
                                isRoleAllowed ? '' : 'not-allowed'
                              }`
                            "
                          ></span>
                          <span
                            class="series-indicator"
                            :class="event.seriesIndicator"
                            v-if="event.seriesIndicator"
                          ></span>
                          <span
                            class="series-drag"
                            v-if="
                              (event.status == 'planned' ||
                                event.status == 'error') &&
                                (event.seriesIndicator == 'start' ||
                                  event.seriesIndicator == 'startend' ||
                                  event.firstAvailable ||
                                  event.firstThisWeek)
                            "
                          >
                            <v-btn
                              icon
                              elevation="0"
                              small
                              color="accent"
                              @mousedown="
                                event.seriesIndicator
                                  ? startDragSeries(event)
                                  : startDrag({ event, timed: true })
                              "
                            >
                              <v-icon>bhi-drag-dots</v-icon>
                            </v-btn>
                          </span>
                        </div>
                      </template>
                      <v-list class="timed-release-context-menu">
                        <v-list-item link dense @click="handleEdit(event)">
                          <v-list-item-icon>
                            <v-icon v-if="event.status === 'sent'"
                              >bhi-eye</v-icon
                            >
                            <v-icon v-else>bhi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title v-if="event.status === 'sent'"
                              >View</v-list-item-title
                            >
                            <v-list-item-title v-else>Edit</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item
                          link
                          dense
                          @click="handleDuplicate(event)"
                          v-if="isRoleAllowedDuplicateItem"
                        >
                          <v-list-item-icon>
                            <v-icon>bhi-copy</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Duplicate</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                  <template v-slot:day-body="{ date }">
                    <div
                      v-if="date == procedureDate"
                      class="procedure-date"
                    ></div>
                  </template>
                  <template
                    v-slot:day-label-header="{
                      date,
                      month,
                      day,
                      weekday,
                      past,
                      present,
                      future,
                    }"
                  >
                    <div :class="{ past, present, future }">
                      {{ day }}
                    </div>
                  </template>
                </v-calendar>
                <v-sheet
                  v-if="isPrevWeekAllowed"
                  :class="dragWeekAreaOpen ? 'visible' : ''"
                  @mouseenter="prevWeekT"
                  @mouseleave="
                    dragWeekHover = false
                    resetDragTimers()
                  "
                  class="drag-over left"
                >
                  <v-progress-circular
                    color="accent"
                    :value="dragWeekHoverTimerLeft"
                    :class="dragWeekHoverTimerReset ? 'reset' : ''"
                  >
                    <v-icon>bhi-arrow-left</v-icon>
                  </v-progress-circular>
                </v-sheet>
                <v-sheet
                  v-if="isNextWeekAllowed"
                  :class="dragWeekAreaOpen ? 'visible' : ''"
                  @mouseenter="nextWeekT"
                  @mouseleave="
                    dragWeekHover = false
                    resetDragTimers()
                  "
                  class="drag-over right"
                >
                  <v-progress-circular
                    color="accent"
                    :value="dragWeekHoverTimerRight"
                    :class="dragWeekHoverTimerReset ? 'reset' : ''"
                  >
                    <v-icon>bhi-arrow-right</v-icon>
                  </v-progress-circular>
                </v-sheet>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          fixed
          bottom
          right
          fab
          large
          class="elevation-0"
          @click="openNewMessageDialog"
        >
          <v-icon size="24">mdi-plus</v-icon>
        </v-btn>
        <new-message-dialog
          :key="newMessageDialogKey"
          :show-dialog="showNewMessageDialog"
          :participantId="[currentPatient.profileId]"
          :participantTz="currentPatient.tz"
          @close-dialog="closeNewMessageDialog"
          @on-submit="createTimedReleaseDefinition"
        />
        <edit-message-dialog
          :show-dialog="showEditMessageDialog"
          :readonly="
            editMessageDialogReadonly && !showEditMessageWithErrorMessage
          "
          :allow-change-only-date-time="showEditMessageWithErrorMessage"
          :participantId="[currentPatient.profileId]"
          :participantTz="currentPatient.tz"
          @close-message-dialog="closeEditMessageDialog"
          @on-submit="editChangeTimedReleaseDefinition"
          @on-delete="prepareDeleteTimedReleaseMessageDefinition"
          @on-delete-template="
            prepareDeleteTemplateTimedReleaseMessageDefinition
          "
          @reload-resource="reloadResource"
          @reload-image-resource="reloadImageResource"
          @show-bigger-image="showBiggerImage"
          :message-definition="timedReleaseMessagesDefinitions"
          :get-resource="getResource"
          :reload-image-resource="reloadImageResource"
          :resource="resource"
          :message-definition-resource="timedReleaseMessagesDefinitionResource"
        />
        <edit-message-dialog
          :show-dialog="showDuplicateDialog"
          :readonly="duplicateDialogReadonly"
          :allow-change-only-date-time="true"
          editType="DUPLICATE"
          :participantId="[currentPatient.profileId]"
          :participantTz="currentPatient.tz"
          @close-message-dialog="closeDuplicateDialog"
          @on-submit="duplicateTimedReleaseDefinition"
          @reload-resource="reloadResource"
          @reload-image-resource="reloadImageResource"
          @show-bigger-image="showBiggerImage"
          :message-definition="duplicateMessageDefinition"
          :get-resource="getResource"
          :reload-image-resource="reloadImageResource"
          :resource="resource"
          :message-definition-resource="timedReleaseMessagesDefinitionResource"
        />
        <templates-dialog
          :show-dialog="showTemplatesDialog"
          @close-dialog="closeTemplatesDialog"
          @on-submit="applyTemplate"
        />
        <v-dialog
          v-model="messageSplitConfirmationDialog"
          persistent
          max-width="290"
          @click:outside="onClickOutsideSplitConfirmation"
          @keydown="onKeydownSplitConfirmation"
        >
          <v-card>
            <v-card-title class="headline">Message split</v-card-title>
            <v-card-text
              >Changing time or date of a single message will cause a split of
              message series. Are you sure you want to do that?</v-card-text
            >
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="rounded-pill" text @click="cancelSplit"
                >Cancel</v-btn
              >
              <v-btn
                class="rounded-pill elevation-0"
                color="accent"
                @click="splitMessages"
                >Yes</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="messageDeleteConfirmationDialog"
          persistent
          max-width="290"
          @click:outside="onClickOutsideDeleteConfirmation"
          @keydown="onKeydownDeleteConfirmation"
        >
          <v-card>
            <v-card-title class="headline">Message delete</v-card-title>
            <v-card-text
              >Do you want to delete this message definition and all its
              messages?</v-card-text
            >
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="rounded-pill" text @click="cancelDelete"
                >Cancel</v-btn
              >
              <v-btn
                class="rounded-pill elevation-0"
                color="accent"
                @click="deleteMessages"
                >Yes</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="messageTemplateDeleteConfirmationDialog"
          persistent
          max-width="290"
          @click:outside="onClickOutsideDeleteConfirmation"
          @keydown="onKeydownDeleteConfirmation"
        >
          <v-card>
            <v-card-title class="headline">Message delete</v-card-title>
            <v-card-text
              >Do you want to delete this message definition and all messages
              created by sprint application?</v-card-text
            >
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="rounded-pill" text @click="cancelDelete"
                >Cancel</v-btn
              >
              <v-btn
                class="rounded-pill elevation-0"
                color="accent"
                @click="deleteTemplateMessages"
                >Yes</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import Calendar from '@/components/journal/Calendar.vue'
import NewMessageDialog from '@/components/dialogs/NewTimedReleaseMessageDefinitionDialog.vue'
import EditMessageDialog from '@/components/dialogs/TimedReleaseMessageDefinitionDialog.vue'
import TemplatesDialog from '@/components/dialogs/TemplatesDialog.vue'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import { debounce } from '@/helpers/debounce'

const DIFF_BETWEEN_CLICK_AND_DRAG = 200

// @ is an alias to /src
export default {
  name: 'PatientTimedRelease',
  components: {
    Calendar,
    NewMessageDialog,
    EditMessageDialog,
    TemplatesDialog,
    PreviewZoomDialog,
  },
  data: () => ({
    today: new Date(),
    selectedDate: {
      year: '',
      month: '',
      day: '',
    },
    isEditing: false,
    awaitingChange: false,
    ready: false,
    value: '',
    events: [],
    messageSplitConfirmationDialog: false,
    messageDeleteConfirmationDialog: false,
    messageTemplateDeleteConfirmationDialog: false,
    showNewMessageDialog: false,
    showEditMessageDialog: false,
    editMessageDialogReadonly: false,
    showEditMessageWithErrorMessage: false,
    showTemplatesDialog: false,
    savingNewPosition: false,
    dragEvent: false,
    dragEventCopy: false,
    dragSeries: false,
    dragStartDragTime: 0,
    clickOpenContextStart: 0,
    clickOpenContextItem: null,
    movedToDay: null,
    deleteMessageDefinitionId: false,
    newMessageDialogKey: 0,
    dialogType: 0,
    imageSrc: false,
    imageName: null,
    dialogFile: null,
    dialogFileMlUrl: null,
    dragWeekAreaOpen: false,
    dragWeekHoverTimerLeft: 0,
    dragWeekHoverTimerRight: 0,
    dragWeekHover: false,
    dragWeekHoverTimerReset: false,
    showContextMenu: {},
    contextMenuX: 0,
    contextMenuY: 0,
    showDuplicateDialog: false,
    duplicateDialogReadonly: false,
    duplicateMessageDefinition: null,
  }),
  computed: {
    ...mapState('patientsStates', [
      'currentPatient',
      'timedReleaseMessages',
      'timedReleaseMessagesDefinitions',
      'timedReleaseMessagesDefinitionResource',
      'timedReleaseMessagesDates',
    ]),
    ...mapState('resourcesStates', ['resource']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    timedReleaseMessagesSorted() {
      return this.groupByKey(this.timedReleaseMessages, 'definitionId')
    },
    cal() {
      return this.ready ? this.$refs.calendar : null
    },
    nowY() {
      return this.cal ? this.cal.timeToY(this.cal.times.now) + 'px' : '-10px'
    },
    dateFormattedAPI() {
      let tmpDate = {
        year: this.selectedDate.year,
        month: this.selectedDate.month - 1,
        day: this.selectedDate.day,
      }
      let date = this.$moment(tmpDate)
      return date.format('YYYY-MM-DD')
    },
    weekStart() {
      return this.$moment(this.dateFormattedAPI)
        .startOf('week')
        .format('YYYY-MM-DD')
    },
    weekStartExtended() {
      // we need to pull more days for messages to detect if there were additional messages in past week or are upcoming in the next one
      return this.$moment(this.dateFormattedAPI)
        .startOf('week')
        .subtract(7, 'days')
        .format('YYYY-MM-DD')
    },
    weekEnd() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('week')
        .format('YYYY-MM-DD')
    },
    weekEndExtended() {
      return this.$moment(this.dateFormattedAPI)
        .endOf('week')
        .add(7, 'days')
        .format('YYYY-MM-DD')
    },
    profileId() {
      return this.currentPatient.profileId
    },
    procedureDate() {
      if (this.currentPatient.procedureDate)
        return this.$moment(this.currentPatient.procedureDate).format(
          'YYYY-MM-DD',
        )
      else return false
    },
    isRoleAllowed() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] !==
        this.dictionaryBhUsersRoleIds.Consultant
      )
    },
    isRoleAllowedDuplicateItem() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    isPrevWeekAllowed() {
      // disabled by https://itsorce.atlassian.net/browse/MVP4BH-4124?focusedCommentId=25784 and https://itsorce.atlassian.net/browse/MVP4BH-4126?focusedCommentId=25873
      // return this.$moment(this.dateFormattedAPI)
      //   .subtract(7, 'days')
      //   .isAfter(this.$moment(this.today).startOf('week'))
      return true
    },
    isNextWeekAllowed() {
      // disabled by https://itsorce.atlassian.net/browse/MVP4BH-4124?focusedCommentId=25784 and https://itsorce.atlassian.net/browse/MVP4BH-4126?focusedCommentId=25873
      // return this.$moment(this.dateFormattedAPI)
      //   .add(7, 'days')
      //   .isAfter(this.$moment(this.today).startOf('week'))
      return true
    },
  },
  mounted() {
    this.ready = true
    if (this.profileId) this.loadMessages()
    this.updateTime()
    this.scrollToTime()
  },
  created() {
    this.selectedDate = {
      year: this.today.getFullYear(),
      month: this.today.getMonth() + 1,
      day: this.today.getDate(),
    }
  },
  watch: {
    profileId(value) {
      if (value) {
        this.loadMessages()
      }
    },
    timedReleaseMessagesSorted(value) {
      if (value) {
        let events = []
        Object.entries(this.timedReleaseMessagesSorted).forEach(
          ([key, el], i, a) => {
            if (Array.isArray(el)) {
              let earliestNotSent = false
              let firstThisWeek = false
              el.forEach((item, index, arr) => {
                let thisWeek = false
                if (
                  this.$moment(item.plannedSendDateLocal).isBetween(
                    this.$moment(this.weekStart),
                    this.$moment(this.weekEnd).add(1, 'days'),
                  ) &&
                  !item.sentDate
                ) {
                  thisWeek = true
                }
                if (thisWeek && !firstThisWeek) {
                  firstThisWeek = item
                }
                if (!item.sentDate && !item.error) {
                  if (earliestNotSent) {
                    if (
                      item.plannedSendDateLocal <
                      earliestNotSent.plannedSendDateLocal
                    )
                      earliestNotSent = item
                  } else {
                    earliestNotSent = item
                  }
                }
              })
              el.forEach((item, index, arr) => {
                let start = new Date(item.plannedSendDateLocal).getTime()
                let color = '#00A0E0'
                let seriesIndicator = false
                let status = 'planned'
                let sentTime = false
                let error = false
                let thisWeek = false
                let isProcedureDateRelated = false

                if (
                  this.$moment(item.plannedSendDateLocal).isBetween(
                    this.$moment(this.weekStart),
                    this.$moment(this.weekEnd).add(1, 'days'),
                  )
                ) {
                  thisWeek = true
                }

                if (item.sentDate && !item.error) {
                  // message was already sent or had an error
                  // start = new Date(item.sentDate).getTime() - we do not use it for now, displaying planned date on the calendar
                  color = '#45C2F4'
                  status = 'sent'
                  sentTime = this.$moment(item.sentDateLocal).format('HH:mm A')
                }

                if (item.error) {
                  error = item.error
                  color = '#FF7E77'
                  status = 'error'
                }
                const compDate = this.$moment(item.plannedSendDateLocal).format(
                  'YYYY-MM-DD',
                )

                if (
                  !item.sentDate &&
                  !item.error &&
                  item.definitionFrom != item.definitionTo
                ) {
                  if (
                    item.plannedSendDateLocal ==
                    earliestNotSent.plannedSendDateLocal
                  ) {
                    if (item.definitionTo == compDate)
                      seriesIndicator = 'startend'
                    else seriesIndicator = 'start'
                  } else if (item.definitionTo == compDate) {
                    seriesIndicator = 'end'
                  } else {
                    seriesIndicator = 'mid'
                  }
                }
                let firstThisWeekDrag = false
                if (
                  firstThisWeek.plannedSendDateLocal ==
                  item.plannedSendDateLocal
                )
                  firstThisWeekDrag = true

                if (item.definitionIsProcedureDateRelated)
                  isProcedureDateRelated = true

                const end = start + 110000 * 60 //15000*60 //110 min default time slot
                const timed = 1

                events.push({
                  name: item.message,
                  seriesIndicator: seriesIndicator,
                  color: color,
                  definitionId: key,
                  definitionFrom: item.definitionFrom,
                  definitionTo: item.definitionTo,
                  status: status,
                  sentTime: sentTime,
                  error: error,
                  firstThisWeek: firstThisWeekDrag,
                  resourceId: item.resourceId,
                  staticMediaId: item.staticMediaId,
                  thisWeek: thisWeek,
                  start,
                  plannedStart: item.plannedSendDateLocal,
                  isProcedureDateRelated: isProcedureDateRelated,
                  end,
                  timed,
                })
                a[i][1][index].start = start // 1 should be improved, we are using here object.entries, that's why we have array("18", array(.. messages ..)) where 18 is definitionId and array with messages is second
                a[i][1][index].end = end
              })
            }
          },
        )

        // if timedReleaseMessages were reloaded from api while we were dragging a message, we need to create a new copy of this message in events array to be able to view it in new week scope. We also need to remove that message if it already exists, but only if it is not a part of series (a case when you changed week forth and back)
        if (
          this.dragging ||
          (this.dragEvent?.error && !this.savingNewPosition)
        ) {
          if (
            events.filter(
              item => item.definitionId == this.dragEvent.definitionId,
            ).length == 1
          ) {
            const itemIndex = events.findIndex(
              item => item.definitionId == this.dragEvent.definitionId,
            )
            events.splice(itemIndex, 1)
          }
          events.push(this.dragEvent) // works for single message, not for series
        }
        //create whole day for procedure
        if (this.currentPatient.procedureDate)
          events.push({
            name: 'Target',
            color: '#FF7E77',
            start: this.$moment(this.currentPatient.procedureDate).format(
              'YYYY-MM-DD',
            ),
          })

        this.events = [...events]
      }
    },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPatient(to.params.id)
      }
    },
  },
  methods: {
    ...mapActions('patientsStates', [
      'getTimedReleaseMessages',
      'getTimedReleaseMessagesDefinition',
      'addTimedReleaseMessagesDefinition',
      'changeTimedReleaseMessagesDefinition',
      'splitTimedReleaseMessagesDefinition',
      'deleteTimedReleaseMessagesDefinition',
      'deleteMessagesByCorrelationId',
      'getTimedReleaseMessagesDefinitionResource',
      'getPatient',
      'getTimedReleaseMessagesCalendar',
      'copyTimedReleaseMessagesDefinition',
    ]),
    ...mapActions('templatesStates', ['applyTemplateOnParticipant']),
    ...mapActions('resourcesStates', ['getResource', 'getResourceByUrl']),
    ...mapMutations('patientsStates', [
      'setTimedReleaseMessagesDefinitions',
      'setTimedReleaseMessagesDefinitionResource',
    ]),
    ...mapMutations('appStates', ['setSnackbarNotification']),
    formatEventTime(date) {
      // calendar is returning time in eventParsed without AM/PM and moment.js can't fix it, so this function returns time in correct AM/PM format
      return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    },
    groupByKey(array, key) {
      if (array)
        return array.reduce((hash, obj) => {
          if (obj[key] === undefined) return hash
          return Object.assign(hash, {
            [obj[key]]: (hash[obj[key]] || []).concat(obj),
          })
        }, {})
      else return false
    },
    getStartingTime() {
      return this.cal ? 7 * 60 : 0 // setting 7 AM as default, can be changed into current time with this.cal.times.now.hour * 60 + this.cal.times.now.minute
    },
    // Disabled by https://itsorce.atlassian.net/browse/MVP4BH-4126?focusedCommentId=25873
    // getParticipantTime() {
    //   const currentTime = this.today.getTime()
    //   const currentDateFormat = this.$moment(currentTime).format(
    //     'YYYY-MM-DD HH:mm:ss',
    //   )
    //   const patientDateFormat = this.$moment(currentTime)
    //     .tz(this.currentPatient.tz)
    //     .format('YYYY-MM-DD HH:mm:ss')
    //   const calculateDiff = this.$moment(patientDateFormat).diff(
    //     this.$moment(currentDateFormat),
    //   )

    //   return new Date(currentTime + calculateDiff).getTime()
    // },
    getCurrentTime() {
      return this.cal
        ? this.cal.times.now.hour * 60 + this.cal.times.now.minute
        : 0
    },
    scrollToTime() {
      const time = this.getStartingTime()
      const first = Math.max(0, time - (time % 30) - 30)
      this.cal.scrollToTime(first)
    },
    updateTime() {
      setInterval(() => this.cal.updateTimes(), 60 * 1000)
    },
    selectCalendar({ month, year }) {
      let date = this.$moment({ year: year, month: month - 1, day: 1 })
      this.getTimedReleaseMessagesCalendar({
        id: this.profileId,
        from: date.format('YYYY-MM-01'),
        to: date.endOf('month').format('YYYY-MM-DD'),
      })
    },
    loadMessagesCalendar(dateFrom) {
      const month = dateFrom
        ? this.$moment(dateFrom).month() + 1
        : this.selectedDate.month
      const year = dateFrom
        ? this.$moment(dateFrom).year()
        : this.selectedDate.year

      this.selectCalendar({
        month,
        year,
      })
    },
    loadMessages() {
      if (this.ready) {
        this.getTimedReleaseMessages({
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        }).then(x => {
          this.loadMessagesCalendar()
        })
      }
    },
    createTimedReleaseDefinition(data) {
      this.addTimedReleaseMessagesDefinition({
        id: this.profileId,
        data: data,
        params: {
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        },
      }).then(() => {
        this.loadMessagesCalendar(data.from)
      })
    },
    selectDate(newDate) {
      this.selectedDate = { ...newDate }
    },
    openNewMessageDialog() {
      this.showNewMessageDialog = true
    },
    closeNewMessageDialog() {
      this.newMessageDialogKey += 1
      this.showNewMessageDialog = false
    },
    openEditMessageDialog(id, readonly = false, messageHasError = false) {
      this.getTimedReleaseMessagesDefinition({ id: id }).then(() => {
        this.showEditMessageDialog = true
        this.showEditMessageWithErrorMessage = messageHasError
        if (readonly) this.editMessageDialogReadonly = true
        if (this.timedReleaseMessagesDefinitions.resourceId)
          this.getTimedReleaseMessagesDefinitionResource({
            id: this.timedReleaseMessagesDefinitions.resourceId,
          })
      })
    },
    closeEditMessageDialog() {
      this.setTimedReleaseMessagesDefinitions(false)
      this.setTimedReleaseMessagesDefinitionResource(false)
      this.showEditMessageDialog = false
      this.showEditMessageWithErrorMessage = false
      this.editMessageDialogReadonly = false
    },
    closeTemplatesDialog() {
      this.showTemplatesDialog = false
    },
    getEventColor(event) {
      return event.color
    },

    startDrag({ event, timed }) {
      this.dragStartDragTime = new Date().getTime()
      if (event.status === 'sent') return
      if (
        !this.dragSeries &&
        (event.status == 'planned' || event.status === 'error')
      ) {
        this.dragWeekAreaOpen = true //TODO change to based on mouse position to left or right border of calendar
      }
      if (event && timed && !this.dragSeries) {
        if (this.timedReleaseMessagesSorted[event.definitionId].length > 1) {
          const newEvent = {
            name: event.name,
            color: event.color,
            definitionId: event.definitionId,
            definitionFrom: event.definitionFrom,
            definitionTo: event.definitionTo,
            start: event.start,
            end: event.end,
            timed: true,
            temporary: true,
            status: 'planned',
          }
          this.events.push(newEvent)
          this.dragEvent = newEvent
          this.dragEventCopy = { ...newEvent }
        } else {
          this.dragEvent = event
          this.dragEventCopy = { ...event }
        }
      } else if (this.dragSeries) {
        this.dragEvent = this.dragSeries
        this.dragEventCopy = this.dragSeries.map(a => {
          return { ...a }
        }) // we need a cloned copy of messages, without any references, so we can restore them later on cancel
      }
      this.dragTime = null
    },
    startDragSeries(event) {
      this.dragStartDragTime = new Date().getTime()
      this.dragSeries = this.events.filter(e => {
        return (
          e.definitionId == event.definitionId &&
          (e.status == 'planned' || e.status === 'error')
        ) // we do not allow to drag sent messages when dragging whole series
      })
    },
    startTime(tms) {
      setTimeout(() => {
        const mouse = this.toTime(tms)
        if (this.dragEvent && this.dragTime === null) {
          let start = 0
          if (Array.isArray(this.dragEvent)) {
            start = this.dragEvent[0].start
          } else {
            start = this.dragEvent.start
          }

          this.dragTime = mouse - start // dragTime means distance in miliseconds from top border of event box frame to the position of mouse cursor in the starting moment of drag
          this.dragging = false
        }
      }, Math.max(1, new Date().getTime() - this.dragStartDragTime))
    },
    mouseMove(tms) {
      const canMove =
        new Date().getTime() - this.dragStartDragTime >
        DIFF_BETWEEN_CLICK_AND_DRAG
      if (!canMove) {
        return
      }

      const newDate = tms?.date

      const mouse = this.toTime(tms)
      if (this.dragEvent && this.dragTime !== null) {
        if (Array.isArray(this.dragEvent)) {
          // Disabled by https://itsorce.atlassian.net/browse/MVP4BH-4126?focusedCommentId=25873
          // if (
          //   mouse - this.dragTime - this.getParticipantTime() >
          //   0 + 1000 * 60 * 15
          // ) {
          let thisEventNewDate = this.$moment(newDate).subtract(1, 'days')
          this.dragEvent.forEach((event, index) => {
            thisEventNewDate = thisEventNewDate.add(1, 'days')
            const start = event.start
            const end = event.end
            const duration = end - start
            const newStartTime =
              mouse - this.dragTime + index * 1000 * 60 * 60 * 24 // when dragging multiple messages at once, we need to include in calculation a difference (in miliseconds) of each day in a serie
            const newStart = this.roundTime(
              newStartTime,
              true,
              thisEventNewDate.unix() * 1000,
            )
            const newEnd = newStart + duration
            if (event.start != newStart) this.dragging = true
            event.start = newStart
            event.end = newEnd
          })
          this.movedToDay = newDate
          // }
        } else {
          // Disabled by https://itsorce.atlassian.net/browse/MVP4BH-4126?focusedCommentId=25873
          // if (
          //   mouse - this.dragTime - this.getParticipantTime() >
          //   0 + 1000 * 60 * 15
          // ) {
          const start = this.dragEvent.start
          const end = this.dragEvent.end
          const duration = end - start
          const newStartTime = mouse - this.dragTime
          const newStart = this.roundTime(newStartTime, true, newDate)
          const newEnd = newStart + duration
          if (this.dragEvent.start != newStart) this.dragging = true
          this.dragEvent.start = newStart
          this.dragEvent.end = newEnd
          this.movedToDay = newDate
          // }
        }
      }
    },
    endDrag() {
      const canMove =
        new Date().getTime() - this.dragStartDragTime >
        DIFF_BETWEEN_CLICK_AND_DRAG
      if (!canMove) return

      this.dragStartDragTime = 0
      /* due to bug in vuetify v-calendar events implementation https://github.com/vuetifyjs/vuetify/issues/9485 we cannot access nativeEvent and use stopPropagation, so click event would be disabled in some circumstances. I am creating ugly fix with setTimeout for that (this.cancelDragging) until vuetify fixes that */
      if (this.dragEvent && this.dragging && !this.dragSeries) {
        if (
          this.dragEvent.definitionId >= 0 &&
          this.timedReleaseMessagesSorted?.[this.dragEvent.definitionId] &&
          this.timedReleaseMessagesSorted[this.dragEvent.definitionId].length >
            1
        ) {
          // we have more messages for this definition, so it is a part of a series, we need to open split confirmation popup
          this.messageSplitConfirmationDialog = true
          this.messageSplit = this.dragEvent
        } else {
          // this message is a single message, we can move it around freely
          this.prepareEditMessageDefinition([this.dragEvent])
        }
      } else if (this.dragEvent && this.dragging && this.dragSeries) {
        this.prepareEditMessageDefinition(this.dragSeries)
        this.dragSeries = null
      } else {
        if (this.dragEvent && this.dragEvent.temporary) {
          const i = this.events.indexOf(this.dragEvent)
          if (i !== -1) {
            this.events.splice(i, 1)
          }
        }
      }
      this.cancelDragging()
    },
    async cancelDragging() {
      this.dragStartDragTime = 0
      await setTimeout(() => {
        this.dragTime = null
        this.dragging = false
        this.dragWeekAreaOpen = false
        this.dragWeekHover = false
        this.resetDragTimers()
      }, 100)
    },
    cancelDrag(showErrors = true) {
      setTimeout(() => {
        if (
          this.dragEvent &&
          !this.messageSplitConfirmationDialog &&
          !this.dragWeekHover &&
          !this.awaitingChange
        ) {
          if (this.dragEvent) {
            if (this.dragEvent.temporary) {
              const i = this.events.indexOf(this.dragEvent)
              if (i !== -1) {
                this.events.splice(i, 1)
              }
            } else {
              if (Array.isArray(this.dragEvent)) {
                this.dragEvent.forEach(item => {
                  const i = this.events.indexOf(item)
                  if (i !== -1) {
                    this.events.splice(i, 1)
                  }
                })
                this.dragEventCopy.forEach(item => {
                  this.events.push(item)
                })
                this.dragEventCopy = null
                if (showErrors)
                  this.setSnackbarNotification(
                    'Messages were dragged outside calendar area. Scheduled times and dates were restored to initial values.',
                  )
              } else {
                const i = this.events.indexOf(this.dragEvent)
                if (i !== -1 && this.dragging) {
                  this.events.splice(i, 1)
                  this.events.push(this.dragEventCopy)
                  const localdate = this.$moment(
                    this.dragEventCopy.start,
                  ).format('MM/DD/YYYY hh:mm A')
                  if (showErrors)
                    this.setSnackbarNotification(
                      'Message was dragged outside calendar area. Scheduled date and time for were restored to ' +
                        localdate +
                        '.',
                    )
                  this.dragEventCopy = null
                }
              }
            }
          }
          this.savingNewPosition = false
          this.dragging = false
          this.dragTime = null
          this.dragEvent = null
          this.dragSeries = null
          this.cancelDragging()
        }
      }, 10)
    },
    roundTime(time, down = true, date = undefined) {
      const roundTo = 15 // minutes
      const roundDownTime = roundTo * 60 * 1000

      const newTime = down
        ? time - (time % roundDownTime)
        : time + (roundDownTime - (time % roundDownTime))

      if (date) {
        const calculated = this.$moment(time)
        const newDate = this.$moment(date)

        const diff = calculated.diff(newDate, 'days', true)

        if (diff < 0) {
          newDate.set({ hours: 0, minutes: 0 })

          return newDate.unix() * 1000
        } else if (diff > 1) {
          newDate.set({ hours: 23, minutes: 45 })

          return newDate.unix() * 1000
        }
      }

      return newTime
    },
    toTime(tms) {
      return new Date(
        tms.year,
        tms.month - 1,
        tms.day,
        tms.hour,
        tms.minute,
      ).getTime()
    },
    cancelSplit() {
      this.messageSplitConfirmationDialog = false
      this.cancelDrag()
    },
    splitMessages() {
      this.messageSplitConfirmationDialog = false
      this.splitTimedReleaseMessagesDefinition({
        id: this.messageSplit.definitionId,
        params: {
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        },
      })
      this.cancelDrag()
    },
    prepareEditMessageDefinition(messages) {
      this.awaitingChange = true
      let firstThisWeek = messages.findIndex(item => item.firstThisWeek)
      const startDate = this.$moment(
        messages[firstThisWeek].plannedStart,
      ).format('YYYY-MM-DD')
      const dayDifference = this.$moment(this.$moment(this.movedToDay)).diff(
        startDate,
        'days',
      )
      const startTime = this.$moment(startDate).add(dayDifference, 'days')
      const endTime = this.$moment(messages[0].definitionTo).add(
        dayDifference,
        'days',
      )

      const start = this.$moment(startTime).format('YYYY-MM-DD')
      const end = this.$moment(endTime).format('YYYY-MM-DD')
      const time = this.$moment(messages[0].start).format('HH:mm:ss')

      this.savingNewPosition = true
      this.changeTimedReleaseMessagesDefinition({
        id: messages[0].definitionId,
        data: {
          from: start,
          to: end,
          time: time,
          message: messages[firstThisWeek].name,
          isProcedureDateRelated:
            messages[firstThisWeek].isProcedureDateRelated,
          resourceId: messages[firstThisWeek].resourceId,
          staticMediaId: messages[firstThisWeek].staticMediaId,
          daysOfWeek: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          },
        },
        params: {
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        },
      })
        .catch(() => {
          this.loadMessages()
        })
        .finally(() => {
          this.savingNewPosition = false
          this.awaitingChange = false
          this.dragEvent = null
          this.dragEventCopy = null
          this.dragging = false
        })
    },
    editChangeTimedReleaseDefinition(data) {
      this.changeTimedReleaseMessagesDefinition({
        id: data.id,
        data: {
          from: data?.date || data.from,
          to: data?.date || data.to,
          time: data.time,
          message: data.message,
          isProcedureDateRelated: data.isProcedureDateRelated,
          resourceId: data.resourceId,
          staticMediaId: data.staticMediaId,
          thumbnailValue: data.thumbnailValue,
          daysOfWeek: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          },
        },
        params: {
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        },
      }).then(x => {
        //  // do we need to do anything here?
        this.loadMessagesCalendar(data.from)
      })
    },
    prepareDeleteTimedReleaseMessageDefinition(id) {
      this.deleteMessageDefinitionId = id
      this.messageDeleteConfirmationDialog = true
    },
    prepareDeleteTemplateTimedReleaseMessageDefinition(id) {
      this.deleteMessageCorrelationId = id
      this.messageTemplateDeleteConfirmationDialog = true
    },
    cancelDelete() {
      this.messageDeleteConfirmationDialog = false
      this.messageTemplateDeleteConfirmationDialog = false
    },
    deleteMessages() {
      this.messageDeleteConfirmationDialog = false
      if (this.deleteMessageDefinitionId)
        this.deleteTimedReleaseMessagesDefinition({
          id: this.deleteMessageDefinitionId,
          params: {
            id: this.profileId,
            from: this.weekStartExtended,
            to: this.weekEndExtended,
          },
        }).then(() => {
          this.loadMessagesCalendar(this.weekStartExtended)
        })
    },
    deleteTemplateMessages() {
      this.messageTemplateDeleteConfirmationDialog = false
      if (this.deleteMessageCorrelationId)
        this.deleteMessagesByCorrelationId({
          id: this.deleteMessageCorrelationId,
          params: {
            id: this.profileId,
            from: this.weekStartExtended,
            to: this.weekEndExtended,
          },
        })
    },
    reloadResource(id) {
      this.getTimedReleaseMessagesDefinitionResource({ id: id })
    },
    applyTemplate({ templateId, startDate }) {
      this.applyTemplateOnParticipant({
        id: this.currentPatient.profileId,
        templateId: templateId,
        startDate: startDate,
      }).then(() => {
        this.loadMessages()
      })
    },
    showBiggerImage({ url, name, file, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.imageName = name
      this.dialogFile = file
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    async reloadImageResource(id) {
      await this.getTimedReleaseMessagesDefinitionResource({ id: id })
      this.imageSrc = this.timedReleaseMessagesDefinitionResource.fileUrl
      this.imageName = this.timedReleaseMessagesDefinitionResource.title
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.currentFile = null
      this.imageSrc = null
      this.imageName = null
      this.dialogFileMlUrl = null
    },
    openMessageDialog({ nativeEvent, event }) {
      if (!this.dragging && this.isRoleAllowed) {
        let readonly = false
        if (event.status !== 'planned') readonly = true
        this.openEditMessageDialog(
          event.definitionId,
          readonly,
          event.status === 'error',
        )
      }
    },
    nextWeek() {
      this.$nextTick(() => {
        let tmpDate = {
          year: this.selectedDate.year,
          month: this.selectedDate.month - 1,
          day: this.selectedDate.day,
        }
        let date = this.$moment(tmpDate).add(7, 'days')
        let tmpDate2 = {
          year: date.year(),
          month: date.month() + 1, // month returns 0-11, selectedDate uses 1-12
          day: date.date(),
        }
        this.selectedDate = { ...tmpDate2 }

        const mouse = this.toTime({
          ...this.selectedDate,
          hour: 1,
          minute: 0,
        })

        if (this.dragging && this.dragEvent) {
          const start = this.dragEvent.start
          const end = this.dragEvent.end
          const duration = end - start
          const newStartTime = mouse - this.dragTime
          const newStart = this.roundTime(newStartTime)
          const newEnd = newStart + duration
          if (this.dragEvent.start != newStart) this.dragging = true
          this.dragEvent.start = newStart
          this.dragEvent.end = newEnd
        }
      })
    },
    nextWeekT() {
      if (this.dragging || this.dragEvent?.error) {
        this.dragWeekHover = true
        this.dragWeekHoverTimerRight = 100
        debounce(
          () => {
            if (this.dragWeekHover) this.nextWeek()
          },
          1100,
          'NextWeek',
        )
      }
    },
    prevWeek() {
      this.$nextTick(() => {
        let tmpDate = {
          year: this.selectedDate.year,
          month: this.selectedDate.month - 1,
          day: this.selectedDate.day,
        }
        let date = this.$moment(tmpDate).subtract(7, 'days')
        let tmpDate2 = {
          year: date.year(),
          month: date.month() + 1, // month returns 0-11, selectedDate uses 1-12
          day: date.date(),
        }
        this.selectedDate = { ...tmpDate2 }

        const mouse = this.toTime({
          ...this.selectedDate,
          hour: 1,
          minute: 0,
        })

        if (this.dragging && this.dragEvent) {
          const start = this.dragEvent.start
          const end = this.dragEvent.end
          const duration = end - start
          const newStartTime = mouse - this.dragTime
          const newStart = this.roundTime(newStartTime)
          const newEnd = newStart + duration
          if (this.dragEvent.start != newStart) this.dragging = true
          this.dragEvent.start = newStart
          this.dragEvent.end = newEnd
        }
      })
    },
    prevWeekT() {
      if (this.dragging || this.dragEvent?.error) {
        this.dragWeekHover = true
        this.dragWeekHoverTimerLeft = 100
        debounce(
          () => {
            if (this.dragWeekHover && this.dragging) this.prevWeek()
          },
          1100,
          'PrevWeek',
        )
      }
    },
    resetDragTimers() {
      this.dragWeekHoverTimerReset = true
      this.dragWeekHoverTimerLeft = 0
      this.dragWeekHoverTimerRight = 0
      setTimeout(() => {
        this.dragWeekHoverTimerReset = false
      }, 10)
    },
    onClickOutsideSplitConfirmation() {
      this.cancelSplit()
    },
    onKeydownSplitConfirmation(key) {
      if (key.code === 'Escape' && key.isTrusted) this.cancelSplit()
    },
    onClickOutsideDeleteConfirmation() {
      this.cancelDelete()
    },
    onKeydownDeleteConfirmation(key) {
      if (key.code === 'Escape' && key.isTrusted) this.cancelDelete()
    },
    handleEdit(event) {
      this.showContextMenu = {}
      this.openMessageDialog({ nativeEvent: null, event })
    },
    handleDuplicate(event) {
      this.showContextMenu = {}
      this.openDuplicateDialog(event.definitionId)
    },
    startClickOpenContext(item) {
      this.clickOpenContextStart = new Date().getTime()
      this.clickOpenContextItem = item
    },
    stopClickOpenContext(event) {
      const timeNow = new Date().getTime()
      const open =
        timeNow - this.clickOpenContextStart < DIFF_BETWEEN_CLICK_AND_DRAG &&
        this.clickOpenContextItem !== null

      if (!open) return

      this.cancelDrag(false)

      this.openContextMenu(event, this.clickOpenContextItem)
    },
    openContextMenu(event, item) {
      this.contextMenuX = event.clientX
      this.contextMenuY = event.clientY

      this.$nextTick(() => {
        this.showContextMenu[
          `${item?.plannedStart}-${item?.definitionId}`
        ] = true

        this.clickOpenContextItem = null
      })
    },
    openDuplicateDialog(id) {
      this.getTimedReleaseMessagesDefinition({ id: id }).then(() => {
        this.duplicateMessageDefinition = {
          ...this.timedReleaseMessagesDefinitions,
        }
        this.duplicateMessageDefinition.message =
          this.duplicateMessageDefinition.message + ''
        this.duplicateMessageDefinition.date = this.$moment()
          .add(1, 'day')
          .format('YYYY-MM-DD')
        this.duplicateMessageDefinition.time =
          this.duplicateMessageDefinition.time || '09:00:00'
        this.showDuplicateDialog = true
        this.duplicateDialogReadonly = false
        if (this.duplicateMessageDefinition.resourceId)
          this.getTimedReleaseMessagesDefinitionResource({
            id: this.duplicateMessageDefinition.resourceId,
          })
      })
    },
    closeDuplicateDialog() {
      this.duplicateMessageDefinition = null
      this.setTimedReleaseMessagesDefinitions(false)
      this.setTimedReleaseMessagesDefinitionResource(false)
      this.showDuplicateDialog = false
      this.duplicateDialogReadonly = false
    },
    duplicateTimedReleaseDefinition(data) {
      this.copyTimedReleaseMessagesDefinition({
        id: data.id,
        data: {
          date: data.date,
          time: data.time,
        },
        params: {
          id: this.profileId,
          from: this.weekStartExtended,
          to: this.weekEndExtended,
        },
      }).then(() => {
        this.closeDuplicateDialog()
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.v-event-timed {
  user-select: none;
  -webkit-user-select: none;

  &:hover .series-drag {
    display: block !important;
  }

  .series-drag:active,
  .series-drag:hover {
    display: block !important;
  }
}
.patient-timed-release {
  flex: 1;
  padding: 0 10px;
  height: 100%;
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  &__calendar-wrapper {
    position: relative;
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 0;
    & > .row > .col {
      padding-bottom: 0;
    }
  }
  .template-button {
    .v-icon {
      margin-left: 10px;
    }
  }
  &-tz {
    color: $bh-gray--2;
    margin-left: 10px;
    flex-grow: 1;
  }

  .v-calendar-daily {
    max-height: calc(100vh - 232px);

    &__pane {
      overflow: visible !important;
    }

    .v-calendar-daily {
      &__day {
        border-right: 0 !important;
        border-bottom: 0 !important;
        &-interval {
          border-top: 0 !important;
        }
      }
      &_head {
        &-day {
          border-right: 0 !important;
          border-bottom: 0 !important;
          display: flex;
          justify-content: flex-end;
          flex-direction: row-reverse;
          position: relative;
          padding: 19px 9px;
          &::after {
            position: absolute;
            content: '';
            bottom: 9px;
            right: 9px;
            left: 9px;
            border-top: 1px solid $bh-gray--5;
          }
          &.v-present {
            .v-calendar-daily_head-day-label {
              color: $bh-blue;
            }
            &::after {
              border-color: $bh-blue;
            }
          }
          &.v-past {
            .v-calendar-daily_head-day-label {
              color: $bh-black;
            }
            .v-calendar-daily_head-weekday {
              color: $bh-black;
            }
          }
          .v-event {
            margin-left: 10px;
          }
        }
      }
      &__intervals {
        &-head {
          border-right: 0 !important;
        }
        &-body {
          border-right: 0 !important;
        }
      }
    }
    .v-present {
      background: #f7f9ff;
    }
  }
  .v-calendar {
    transition-duration: 300ms;
    &.dragBorder {
      box-shadow: 0px 0px 10px $bh-red--dark;
    }
    &-daily {
      border-top: 0 !important;
      border-left: 0 !important;
      &__head {
      }
      &_head {
        &-weekday {
          font-size: 14px;
          line-height: 20px;
          padding: 0 0 0 4px;
          text-transform: capitalize;
          order: 1;
        }
        &-day-label {
          font-size: 14px;
          font-weight: bold;
          padding: 0;
          line-height: 20px;
          order: 2;
        }
        &-day {
          &.v-present {
            .v-calendar-daily_head-weekday {
              color: $bh-blue;
            }
          }
          &.v-past {
            .v-calendar-daily_head-weekday {
              color: $bh-gray--2 !important;
            }
            .v-calendar-daily_head-day-label {
              color: $bh-gray--2 !important;
            }
          }
        }
      }
      &__scroll-area {
        overflow: auto;
      }
      &__intervals-head {
        &::after {
          bottom: 9px;
        }
      }
    }
    &-events,
    &-events.theme--light {
      .v-event-timed {
        border: 1px solid $bh-blue--4 !important;
        padding: 0 !important;
        font-size: 12px;
        min-height: 80px !important;
        height: auto !important;
        margin-left: 5px;
        z-index: initial;
        overflow: initial;
        &--wrapper {
          padding: 10px;
        }
        &-container {
          z-index: 1;
        }
        .v-event-draggable {
          padding-top: 20px;
          display: flex;
          height: 100%;
          flex-direction: column;
          justify-content: space-between;
          z-index: 1;
          .v-event-name {
            font-weight: bold;
            overflow: hidden;
            font-size: 16px;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .v-event-time {
            opacity: 0.8;
            font-weight: bold;
          }
        }
        .series-indicator {
          position: absolute;
          background: $bh-blue--2;
          opacity: 0.15;
          top: -8px;
          left: -5px;
          right: -5px;
          bottom: -8px;
          z-index: -1;
          &.start {
            border-radius: 50px 0 0 50px;
            left: 10px;
          }
          &.startend {
            display: none;
          }
          &.end {
            border-radius: 0 50px 50px 0;
            right: 10px;
          }
        }
        .status {
          position: absolute;
          display: block;
          width: 16px;
          height: 16px;
          top: 10px;
          left: 10px;
          border: 2px solid $bh-white;
          border-radius: 50%;
          &.not-allowed {
            right: 10px;
          }
          &.sent {
            &::after {
              content: '';
              display: block;
              width: 8px;
              height: 8px;
              position: absolute;
              top: 2px;
              right: 2px;
              background: $bh-white;
              border-radius: 50%;
            }
          }
          &.error {
            &::after {
              content: '✕';
              display: block;
              width: 12px;
              font-size: 14px;
              height: 14px;
              line-height: 14px;
              text-align: center;
              color: $bh-white;
              position: absolute;
              top: 0px;
            }
          }
        }
        .series-drag {
          position: absolute;
          left: -32px;
          top: 0;
          height: 100%;
          display: none;
          .v-btn {
            height: 100%;
            border-radius: 50% 0 0 50% !important;
            background: rgba(255, 255, 255, 0.5);
          }
        }
      }
    }
  }
  .v-event-timed-container {
    z-index: 1;
  }
  .procedure-date {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: $bh-red;
    opacity: 0.2;
    z-index: 0;
  }
  .navbar {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }
  .drag-over {
    position: absolute;
    // top: 248px;
    top: 0;
    width: 75px;
    bottom: 0;
    background: $bh-blue--2;
    opacity: 0;
    z-index: -1;
    display: flex;
    justify-content: center;
    align-items: center;
    transition-duration: 300ms;
    cursor: pointer;
    &.left {
      left: 0;
      padding-right: 10px;
      background: rgb(0, 0, 0);
      background: -moz-linear-gradient(
        270deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: -webkit-linear-gradient(
        270deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, $bh-blue--2 25%);
    }
    &.right {
      right: 0;
      padding-left: 10px;
      background: rgb(0, 0, 0);
      background: -moz-linear-gradient(
        90deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: -webkit-linear-gradient(
        90deg,
        rgba(0, 0, 0, 0) 0%,
        $bh-blue--2 25%
      );
      background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, $bh-blue--2 25%);
    }
    &.visible {
      opacity: 0.4;
      z-index: 3;
    }
  }
  .v-progress-circular__overlay {
    transition: all 1s linear;
  }
  .v-progress-circular.reset {
    .v-progress-circular__overlay {
      transition: all 0.01s linear !important;
    }
  }

  .context-menu-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 2;
    &::before {
      background-color: $bh-white;
    }
    .v-icon {
      color: $bh-white;
    }
  }
}
.timed-release-context-menu {
  .v-list-item {
    min-height: 40px !important;
  }
  .v-list-item__icon {
    margin-right: 12px !important;
    text-align: center;
    display: block;
    .v-icon {
      color: $bh-blue;
    }
  }
  .v-list-item__content {
    .v-list-item__title {
      font-weight: bold;
      font-size: 1rem;
      color: $bh-blue;
    }
  }
}
</style>
